import classNames from "classnames";
import {
  getVideoPlayerMuted,
  getVideoPlayerVolume,
  setVideoPlayerMuted,
  setVideoPlayerVolume,
} from "components/v2/Videos/VideoPlayer/utils";
import { CONTENT_TYPES } from "constants/phase";
import amplitudeInstance from "externals/amplitude";
import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { selectSelectedEntityId } from "store/entity/selectors";
import { selectSelectedUserId } from "store/user/selectors";
import { isHlsVideo } from "utils/hls";

import {
  selectClipDetailsById,
  selectClipDisplayAspectRatio,
  selectClipDisplayTitleById,
  selectClipDurationById,
  selectClipEditedClipDataPreviewUrlById,
  selectClipEntityId,
  selectClipMp4VideoUrl,
  selectClipStreamIdById,
  selectClipVariantVideoUrlById,
  selectClipVideoUrlByAspectRatio,
} from "../../../../../store/clip/selectors";
import { showClipPreviewModal, showClipReportIssueModal } from "../../../../../store/clip/slice";
import {
  selectClipListAspectRatioById,
  selectClipListDetailsById,
  selectClipListPreviewDataDurationById,
  selectClipListPreviewUrlById,
  selectClipListStreamIdById,
  selectClipListTitleById,
} from "../../../../../store/clipList/selectors";
import {
  selectAppAdditionalPermissionsAllowDirectDownload,
  selectAppAdditionalPermissionsDownloadClipButtonBySelectedUserId,
  selectAppAdditionalPermissionsDownloadHighlightButtonBySelectedUserId,
  selectAppAdditionalPermissionsIsHideRightClickBySelectedUserId,
} from "../../../../../store/entityConfig/selectors";
import { getCloudPreSignedURL } from "../../../../../store/publisher/actions";
import ClipDownload from "../../../Buttons/Clip/ClipDownload";
import SVGIcon from "../../../Images/SVGIcon";
import Modal from "../../../Modal";
import ModalBody from "../../../Modal/ModalBody";
import ModalFooter from "../../../Modal/ModalFooter";
import ModalTitle from "../../../Modal/ModalTitle";
import commonStyles from "../../../style.module.css";
import VideoPlayer from "../../../Videos/VideoPlayer";
import styles from "./style.module.css";

const ClipPreviewModal = ({
  clipId,
  modalType,
  showClipPreviewModal,
  appAdditionalPermissionsDownloadClipButton,
  appAdditionalPermissionsIsHideRightClick,
  aspectRatio,
  clipDisplayAspectRatio,
  clipVideoUrlByAspectRatio,
  clipEditedClipDataPreviewUrl,
  clipListId,
  clipListAspectRatio,
  clipListPreviewUrl,
  appAdditionalPermissionsDownloadHighlightButton,
  clipMp4VideoUrl,
  downloadDirectly = false,
  clipDetails,
  clipListDetails,
  isGeneratedVideoDownload,
  variantData,
  clipVideoData,
  contentType,
  isEditedClip = false,
  page,
  clipVariantVideoUrl,
  showClipReportIssueModal,
}) => {
  const [volume, setVolume] = useState(getVideoPlayerVolume("ClipPreview", 0.5));
  const [muted, setMuted] = useState(getVideoPlayerMuted("ClipPreview", true));

  const onVolumeChange = useCallback(
    (event) => {
      try {
        const value = event.target.volume;
        const mute = event.target.muted;
        if (value === 0 || mute) {
          setMuted(true);
          setVideoPlayerMuted("ClipPreview", true);
        } else {
          if (muted) {
            setMuted(false);
            setVideoPlayerMuted("ClipPreview", false);
            setVolume(getVideoPlayerVolume("ClipPreview", 0.5));
          } else {
            setVolume(parseFloat(value));
            setVideoPlayerVolume("ClipPreview", parseFloat(value));
          }
        }
      } catch (error) {
        console.error(error);
      }
    },
    [muted],
  );

  const videoUrl = useMemo(() => {
    if (clipListId) {
      return clipListPreviewUrl;
    }
    if (modalType === "previewVariant") {
      return variantData?.videoUrl;
    }

    if (modalType === "clipEdited") {
      return clipEditedClipDataPreviewUrl || clipVideoUrlByAspectRatio;
    }

    if (modalType === "hlsToMp4ClipPreview") {
      return clipMp4VideoUrl;
    }

    if (modalType === "previewExportedClip") {
      return clipVideoData?.videoUrl;
    }

    return clipVariantVideoUrl || clipVideoUrlByAspectRatio;
  }, [
    clipListId,
    modalType,
    clipVariantVideoUrl,
    clipVideoUrlByAspectRatio,
    clipListPreviewUrl,
    variantData,
    clipVideoData,
    clipEditedClipDataPreviewUrl,
    clipMp4VideoUrl,
  ]);

  const displayAspectRatio = useMemo(() => {
    if (clipListId) {
      return clipListAspectRatio;
    }

    return clipDisplayAspectRatio;
  }, [clipDisplayAspectRatio, clipListAspectRatio, clipListId]);

  const canDownload = useMemo(() => {
    if (modalType === "previewExportedClip") {
      // Disable download for HLS videos (.m3u8 or .m3u extension)
      return !isHlsVideo(videoUrl);
    }

    if (["previewClip", "previewClipList", "previewVariant"].includes(modalType)) {
      return true;
    }

    if (clipListId) {
      return appAdditionalPermissionsDownloadHighlightButton;
    }

    if (modalType === "hlsToMp4ClipPreview" || modalType === "publishedClipPreview") {
      return false;
    }

    return appAdditionalPermissionsDownloadClipButton;
  }, [
    modalType,
    appAdditionalPermissionsDownloadClipButton,
    appAdditionalPermissionsDownloadHighlightButton,
    clipListId,
    videoUrl,
  ]);

  const controlsList = useMemo(() => {
    return "nodownload";
  }, []);

  const onClose = useCallback(() => {
    try {
      showClipPreviewModal(null);
    } catch (error) {
      console.error(error);
    }
  }, [showClipPreviewModal]);

  const isGif = useMemo(() => {
    return videoUrl?.split(".")?.pop()?.toLowerCase() === "gif";
  }, [videoUrl]);

  const handleReportIssue = useCallback(() => {
    showClipReportIssueModal({
      selectedClips: [clipListId],
      streamId: clipListDetails?.streamId,
      aspectRatio,
      contentType: CONTENT_TYPES.HIGHLIGHT,
      page,
    });

    amplitudeInstance.logEvent("clips_report_issue_modal_triggered", {
      page,
      contentIds: [clipListId],
      aspectRatio,
      contentType: CONTENT_TYPES.HIGHLIGHT,
    });
  }, [showClipReportIssueModal, clipListId, clipListDetails?.streamId, aspectRatio, page]);

  const isStreamIndependentProp = useMemo(() => {
    return Boolean(clipId && clipDetails?.projectId && clipId === clipDetails?.projectId);
  }, [clipId, clipDetails]);

  if (videoUrl) {
    return (
      <Modal
        open={true}
        footer={null}
        className={styles.modalContainer}
        onCancel={onClose}
        closable={!["previewClip", "previewClipList", "previewVariant", "previewExportedClip"].includes(modalType)}
      >
        <ModalTitle className={styles.modalTitle}>Preview</ModalTitle>
        <ModalBody className={styles.modalBody}>
          <div>
            {isGif ? (
              <div className={styles.gifContainer}>
                <SVGIcon src={videoUrl} />
              </div>
            ) : (
              <VideoPlayer
                style={{ height: "100%", width: "auto", maxWidth: 858, maxHeight: "60vh" }}
                url={videoUrl}
                width="100%"
                height="100%"
                volume={volume}
                muted={muted}
                onVolumeChange={onVolumeChange}
                controlsList={controlsList}
                isContextMenuEnabled={appAdditionalPermissionsIsHideRightClick}
                aspectRatio={displayAspectRatio}
              />
            )}
          </div>
        </ModalBody>
        <ModalFooter className={styles.modalFooter}>
          {canDownload ? (
            <ClipDownload
              clipId={clipId}
              aspectRatio={aspectRatio}
              clipListId={clipListId}
              className={classNames(commonStyles.secondaryButton, styles.modalButton)}
              isGeneratedVideoDownload={isGeneratedVideoDownload}
              // needed for the /preview-video page which is public
              clipData={clipDetails}
              clipListData={clipListDetails}
              downloadDirectly={downloadDirectly}
              isStreamIndependentProp={isStreamIndependentProp}
              isPublic={["previewClip", "previewClipList", "previewVariant", "previewExportedClip"].includes(modalType)}
              variantData={variantData}
              contentType={contentType}
              isEditedClip={isEditedClip}
              clipVideoId={clipVideoData?.exportedClipId}
              clipVideoData={clipVideoData}
              page={page}
            >
              <SVGIcon src="components/v2/Cards/ClipCard/download.svg" className={styles.modalButtonIcon} />
              Download
            </ClipDownload>
          ) : null}

          {clipListId && modalType !== "previewClipList" ? (
            <button
              className={classNames(commonStyles.secondaryButton, styles.reportIssueButton)}
              onClick={handleReportIssue}
            >
              <SVGIcon src="components/v2/ActionBar/report-issue.svg" className={styles.modalButtonIcon} />
              Report Issue with Highlight
            </button>
          ) : null}
        </ModalFooter>
      </Modal>
    );
  }

  return null;
};

const mapStateToProps = createStructuredSelector({
  userId: selectSelectedUserId,
  clipDetails: selectClipDetailsById,
  clipListDetails: selectClipListDetailsById,
  entityId: selectSelectedEntityId,
  clipDisplayTitle: selectClipDisplayTitleById,
  clipDuration: selectClipDurationById,
  clipDisplayAspectRatio: selectClipDisplayAspectRatio,
  clipVideoUrlByAspectRatio: selectClipVideoUrlByAspectRatio,
  clipStreamId: selectClipStreamIdById,
  clipEditedClipDataPreviewUrl: selectClipEditedClipDataPreviewUrlById,
  clipListTitle: selectClipListTitleById,
  clipListPreviewDataDuration: selectClipListPreviewDataDurationById,
  clipListPreviewUrl: selectClipListPreviewUrlById,
  clipListStreamId: selectClipListStreamIdById,
  clipListAspectRatio: selectClipListAspectRatioById,
  appAdditionalPermissionsDownloadClipButton: selectAppAdditionalPermissionsDownloadClipButtonBySelectedUserId,
  appAdditionalPermissionsIsHideRightClick: selectAppAdditionalPermissionsIsHideRightClickBySelectedUserId,
  appAdditionalPermissionsDownloadHighlightButton:
    selectAppAdditionalPermissionsDownloadHighlightButtonBySelectedUserId,
  allowDirectDownload: selectAppAdditionalPermissionsAllowDirectDownload,
  clipEntityId: selectClipEntityId,
  clipMp4VideoUrl: selectClipMp4VideoUrl,
  clipVariantVideoUrl: selectClipVariantVideoUrlById,
});

const mapDispatchToProps = { showClipPreviewModal, getCloudPreSignedURL, showClipReportIssueModal };

export default connect(mapStateToProps, mapDispatchToProps)(ClipPreviewModal);
